import { Logo } from "@ondit/tokens/logo";
import { Button, Input } from "@ondit/ui";
import { type FormEvent, useState } from "react";
import { Field } from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { ApiError, api } from "@/lib/api";

/**
 * Where to land after signing in, held to this origin. Resolved by the URL
 * parser rather than by looking at the first characters, because the parser
 * is what the browser will use: `/\evil.example` starts with one slash and
 * is, to a browser, a protocol-relative link to somebody else's site.
 */
function sameOrigin(next: string): string {
	try {
		const url = new URL(next, window.location.origin);
		if (url.origin === window.location.origin) return url.href;
	} catch {
		/* not a URL at all */
	}
	return "/";
}

export function LoginPage() {
	const t = useT();
	const { locale } = useI18n();
	const params = new URLSearchParams(window.location.search);
	const next = params.get("next") ?? "/";
	const [email, setEmail] = useState("");
	const [secret, setSecret] = useState("");
	const [busy, setBusy] = useState(false);
	const [problem, setProblem] = useState<string | null>(null);

	async function submit(event: FormEvent) {
		event.preventDefault();
		setBusy(true);
		setProblem(null);
		try {
			await api.login(email, secret);
			// A full load: the cookie is new and every request has to carry it.
			window.location.assign(sameOrigin(next));
		} catch (error) {
			if (error instanceof ApiError && error.isUnauthorized) setProblem(t("login.failed"));
			else setProblem(error instanceof ApiError ? error.localized(locale) : String(error));
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="login">
			<form className="login-card" onSubmit={submit} aria-busy={busy}>
				<Logo size="lg" />
				<div>
					<h1>{t("login.title")}</h1>
					<p className="lede">{t("login.lede")}</p>
				</div>
				<fieldset className="form" disabled={busy}>
					<Field label={t("login.email")}>
						<Input
							type="email"
							autoComplete="username"
							required
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						/>
					</Field>
					<Field label={t("login.password")}>
						<Input
							type="password"
							autoComplete="current-password"
							required
							value={secret}
							onChange={(e) => setSecret(e.target.value)}
						/>
					</Field>
					{problem ? (
						<div className="banner banner-danger" role="alert">
							{problem}
						</div>
					) : null}
					<Button variant="primary" type="submit" disabled={busy}>
						{t("login.submit")}
					</Button>
				</fieldset>
			</form>
		</div>
	);
}
